import { Component, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { forkJoin } from 'rxjs'
import { ClientInformationAttribute } from 'src/app/models/client-information/ClientInformationAttribute'
import { ClientDto } from 'src/app/models/client/client-dto'
import { SlatAssessmentDto } from 'src/app/models/slat/slat-assessment-dto'
import { SlatAssessmentTableGroupByDto } from 'src/app/models/slat/slat-assessment-table-group-dto'
import { SlatAssessmentTableItemsDto } from 'src/app/models/slat/slat-assessment-table-items-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { FeaturesService } from 'src/app/services/features/features.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import {
  ImpactingFactor,
  ImpactingFactorToDisplayName,
} from 'src/app/services/reddi/reddi-utils'
import { SlatService } from 'src/app/services/slat/slat.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { AreYouSureModalComponent } from '../../complex/modals/are-you-sure-modal/are-you-sure-modal.component'
@Component({
  selector: 'app-slat-instance',
  imports: [],
  templateUrl: './slat-instance.component.html',
  styleUrl: './slat-instance.component.scss',
  standalone: false,
})
export class SlatInstanceComponent implements OnInit {
  isAdd: boolean = true
  buttonLabel: string = this.isAdd ? 'Add' : 'Edit'

  slatTableData: (
    | SlatAssessmentTableItemsDto
    | SlatAssessmentTableGroupByDto
  )[] = []
  slatAssessmentDataDto: SlatAssessmentDto

  displayedColumnsTest: string[] = [
    'Name',
    'RawScore',
    'Score',
    'PercentileRank',
    'Level',
    'Action',
  ]
  dataSource = this.slatTableData

  isGroup(index: number, item: any): boolean {
    return item.isGroupBy
  }

  _clientId: string
  _clientName: string = 'Loading...'
  _title: string = '\u00A0'
  _age: string
  _dateOfBirth: Date = new Date()
  _dataEnteredBy: string
  _soundEnteredByName: string
  _presentationDate: string
  _considerations: string[] = []
  _notes: string = 'No notes entered'
  _templateName: string = 'CELF P3'
  _showClientDetails: boolean = false
  _client: ClientDto
  _dashboardUrl: string
  _slatId: string
  //Client Attributes
  _clientInformationAttributes: ClientInformationAttribute[] =
    this.commonService.createEmptyAttributeList()
  _clientInformationTitle = 'Client Information'
  _clientInformationDisplayText = ''
  _clientInformationUrl = ''
  _clientInFocus: string = ''
  _clientEditEnabled: boolean = false //Perform check based on Feature Flag
  _totalReviews = 0
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }

  constructor(
    public router: Router,
    public spinnerService: SpinnerService,
    public activatedRouter: ActivatedRoute,
    public clientService: ClientService,
    public bannerService: BannerService,
    public slatService: SlatService,
    public dialog: MatDialog,
    public previousRouteService: PreviousRouteService,
    private commonService: CommonService,
    public featureService: FeaturesService,
  ) {}

  ngOnInit(): void {
    this.initializeClientData()
  }

  private initializeClientData(): void {
    this._clientId = this.activatedRouter.snapshot.url[2].path
    this._slatId = this.activatedRouter.snapshot.url[3].path
    this._dashboardUrl = 'client-dashboard/' + this._clientId
    let request = {
      client: this.clientService.Details(this._clientId),
      slatAssessment: this.slatService.GetAssessmentInstanceById(this._slatId),
      features: this.featureService.GetFeatures(),
    }

    this.spinnerService.show()
    forkJoin(request).subscribe({
      next: ({ client, slatAssessment, features }) => {
        this._clientEditEnabled = this.commonService.getFeatureValue(
          features,
          'clientEdit',
        )
        this._client = client
        this._clientName = client.preferredFullName
        this._age = this._client.age
        this._dateOfBirth = this._client.dateOfBirth
        this._soundEnteredByName =
          slatAssessment?.presenterName ?? client?.clientCaseManagerName
        this._title = this._client.preferredFullName
        this._dateOfBirth = this._client.dateOfBirth
        this._showClientDetails = false
        this.setClientInformation(client)
        //assigning headers
        this.slatAssessmentDataDto =
          this.slatService.assignHeaders(slatAssessment)
        //update the LevelItem based on Level
        this.slatAssessmentDataDto = this.slatService.updateLevelItem(
          this.slatAssessmentDataDto,
        )
        this.refreshTableData()
        //tools has access check
        this.checkToolsAccess()
        this.retriveIndicators(client)
        this.processSidebarComponents()
      },
      error: () => {
        this.bannerService.showBanner(
          `Unable to retrieve data.`,
          'error',
          30000,
        )
        this.spinnerService.hide()
      },
      complete: () => {
        this.spinnerService.hide()
      },
    })
  }

  refreshTableData = () => {
    //refresh table data
    this.slatTableData = []
    this.dataSource = this.slatTableData =
      this.slatService.SlatFormatMultiHeadingData(this.slatAssessmentDataDto)
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId, this._slatId)
  }

  processSidebarComponents() {
    this._presentationDate = moment(
      this.slatAssessmentDataDto?.assessmentDate,
    ).format('dddd MMMM Do, yyyy')

    for (
      let i = 0;
      i < this.slatAssessmentDataDto?.impactingFactors?.length;
      i++
    ) {
      if (
        this.slatAssessmentDataDto?.impactingFactors[i] !== ImpactingFactor.None
      ) {
        this._considerations.push(
          ImpactingFactorToDisplayName(
            this.slatAssessmentDataDto?.impactingFactors[i],
          ),
        )
      }
    }
    this._notes = this.slatAssessmentDataDto?.notes
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._client,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._client,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._client,
    )
  }

  retriveIndicators(client: ClientDto) {
    this._totalReviews = this.commonService.returnReviewIndicator(client)
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }

  setClientInformation(client: ClientDto) {
    this._clientInFocus = client.contactId
    this._clientInformationDisplayText = this._clientEditEnabled
      ? 'Edit profile'
      : ''
    this._clientInformationUrl = this._clientEditEnabled
      ? '/edit-client/' + client.contactId
      : ''

    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Age',
      client.age,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'DOB',
      moment(client.dateOfBirth).format('Do MMMM, YYYY'),
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Gender',
      client.genderCodeName,
    )
    this.commonService.setAttribute(
      this._clientInformationAttributes,
      'Case Manager',
      client.clientCaseManagerName,
    )
  }

  cancelAssessment(): void {
    const dialogRef = this.dialog.open(AreYouSureModalComponent, {
      width: '605px',
      height: '316px',
      panelClass: 'are-you-sure-panel',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigateByUrl(this._dashboardUrl)
      } else {
      }
    })
  }
}
